import {
    DeliveryTagInfo,
    DeliveryTagInfoVisibilityLevel,
    ProgramSubTypes,
    ProgramTypes,
} from "@super-ui/interfaces/SwiggyOne";

import { isEmpty, priceInRupee } from "@swiggy-private/core-ui";
import { isOneTap, isRegularNudge, isSuperV3Benefits, isSuperV3Nudge } from "@super-ui/helpers/OneWidget";

import { PlanDetails, RenderingDetails, RenderingDetailsMeta, Benefit } from "@swiggy-private/cart-types";
import { BG_GRADIENTS, DELIVERY_TAG_COLORS, NUDGE_TYPES, RUPEE_SYMBOL, ONELOGO_LINK } from "../constants";
import { PLACEHOLDERS } from "@super-ui/constants";
import { BenefitsListItem, CTA, CTATypes, MovNotMet } from "../types";

export const isOneLite = (planDetails: PlanDetails) => {
    return planDetails?.program === ProgramTypes.ONE && planDetails?.type === ProgramSubTypes.LITE;
};

export const isSwiggyBlack = (planDetails: PlanDetails) => {
    return planDetails?.program === ProgramTypes.ONE && planDetails?.type === ProgramSubTypes.BLACK;
};

export const isOneFlagship = (planDetails: PlanDetails) => {
    return planDetails?.program === ProgramTypes.ONE && planDetails?.type === ProgramSubTypes.PLUS;
};

export const isRegularOneBuyNudge = (key: string, meta: RenderingDetailsMeta) => {
    return isSuperV3Nudge(key) && meta?.nudge_type === NUDGE_TYPES.REGULAR && isOneFlagship(meta?.plan_details);
};

export const isOneBlackActiveUserBenefit = (
    key: string,
    meta: RenderingDetailsMeta,
    activePlanDetails: PlanDetails,
) => {
    return activeOneUserBenefitsWidget(key, meta) && isSwiggyBlack(activePlanDetails);
};

export const activeOneUserBenefitsWidget = (key: string, meta: RenderingDetailsMeta) => {
    return isSuperV3Benefits(key) && !isOneTap(meta) && !isRegularNudge(meta);
};

export const isNudgeTypeOneTap = (meta: RenderingDetailsMeta) => {
    return meta?.nudge_type === NUDGE_TYPES.ONE_TAP;
};

export const isOneLiteBenefitsLowVisibility = (deliveryTagInfo: DeliveryTagInfo) => {
    return deliveryTagInfo?.visibility_level?.toUpperCase() === DeliveryTagInfoVisibilityLevel.LOW;
};

export const isOneLiteBenefitsHighVisibility = (deliveryTagInfo: DeliveryTagInfo) => {
    return deliveryTagInfo?.visibility_level?.toUpperCase() === DeliveryTagInfoVisibilityLevel.HIGH;
};

export const getDeliveryTagInfoColors = (deliveryTagInfo: DeliveryTagInfo) => {
    return isOneLiteBenefitsLowVisibility(deliveryTagInfo) ? DELIVERY_TAG_COLORS.LOW : DELIVERY_TAG_COLORS.HIGH;
};

export const planPriceFormatter = (price?: number): string => {
    // prices is in paise
    return !isEmpty(price) && !isNaN(price) ? `${RUPEE_SYMBOL}${String(priceInRupee(price))}` : "";
};

export const deliveryTagInfoBuilder = (deliveryTagInfo: DeliveryTagInfo, planDetails: PlanDetails) => {
    if (isOneLite(planDetails) && !isEmpty(deliveryTagInfo)) {
        const cleanupBoldFromPillText = deliveryTagInfo?.text
            ?.replace(PLACEHOLDERS.BOLD_START, "")
            .replace(PLACEHOLDERS.BOLD_END, "");
        return {
            pillText: cleanupBoldFromPillText,
            subText: deliveryTagInfo?.sub_text,
            ...getDeliveryTagInfoColors(deliveryTagInfo),
        };
    }
};

export const benefitsListBuilder = (benefits: BenefitsListItem[], extraOfferClickHandler: VoidFunction) => {
    if (!isEmpty(benefits)) {
        return benefits?.map(benefit => {
            const isEOAvailable = benefit?.attribute_values?.behaviour?.toUpperCase() === MovNotMet.MOV_NOT_MET;
            const benefitListBuilderText = `${PLACEHOLDERS.BOLD_START}${benefit?.title}${PLACEHOLDERS.BOLD_END} ${benefit?.sub_title}`;
            return {
                text: isEOAvailable ? benefit?.title : benefitListBuilderText,
                showShimmer: benefit?.show_shimmer || false,
                isEOAvailable,
                extraOfferClickHandler: isEOAvailable ? extraOfferClickHandler : null,
            };
        });
    }
};

/**
 * For Cart Upsell Project we had a requirement to make One Tap widgets behave like Active One User Benefits widget
 * As backend couldn't modify nudge_type from ONE_TAP they started populating ctas as null for OneTapAdd and OneTapRemove during Cart Upsell flow if plan is added to cart
 *
 * Now from portal we have added checks to make OneTap widgets only be shown when ctas are not empty which is a exisiting critical requirement
 * But in Cart Upsell flows we wanted to show OneTap widgets as Active One User Benefits widget so when ctas are empty we are showing them as Active One User Benefits widget
 */
export const allowCartUpsellOneTapRenderAsActive = (key: string, meta: RenderingDetailsMeta) => {
    return isSuperV3Benefits(key) && isNudgeTypeOneTap(meta) && isEmpty(meta?.ctas);
};

/**
 * Collating all the conditions to render Red Gradient with Background Logo for One Flagship
 */
export const renderRedGradientWithBgLogo = (key: string, meta: RenderingDetailsMeta) => {
    return (
        activeOneUserBenefitsWidget(key, meta) ||
        isRegularOneBuyNudge(key, meta) ||
        allowCartUpsellOneTapRenderAsActive(key, meta)
    );
};

export const bgGradientSelector = (meta: RenderingDetailsMeta, key: string, planDetails: PlanDetails) => {
    // Red Gradient with Background Logo for One Flagship - Benefits Widget + Regular Buy Nudge or One Tap Nudge with No CTAs [Cart Upsell Case]
    if (renderRedGradientWithBgLogo(key, meta) && isOneFlagship(planDetails)) {
        return BG_GRADIENTS.RED;
    } else if (
        // Yellow Gradient for One Lite - Benefits Widget + One Tap Remove Nudge + One Tap Nudge with No CTAs [Cart Upsell Case]
        isOneLite(planDetails) &&
        isSuperV3Benefits(key) &&
        // either nudge is non OneTap or it is OneTap and ctas are empty [Cart Upsell Case]
        (!isNudgeTypeOneTap(meta) || allowCartUpsellOneTapRenderAsActive(key, meta)) &&
        isOneLiteBenefitsLowVisibility(meta?.delivery_tag_info)
    ) {
        return BG_GRADIENTS.YELLOW;
    } else if (
        // Green Gradient for One Lite - Benefits Widget + One Tap Remove Nudge + One Tap Nudge with No CTAs [Cart Upsell Case]
        isOneLite(planDetails) &&
        isSuperV3Benefits(key) &&
        // either nudge is non OneTap or it is OneTap and ctas are empty [Cart Upsell Case]
        (!isNudgeTypeOneTap(meta) || allowCartUpsellOneTapRenderAsActive(key, meta)) &&
        isOneLiteBenefitsHighVisibility(meta?.delivery_tag_info)
    ) {
        return BG_GRADIENTS.GREEN;
    } else if (
        isOneFlagship(planDetails) &&
        isSuperV3Benefits(key) &&
        (isNudgeTypeOneTap(meta) || isRegularNudge(meta)) &&
        // due to Upgrade case where checkout sends SUPER_V3_BENEFITS for OneTapAdd as well, we wanted to show the gradient only when plan
        // is added to the cart, as SUPER_V3_BENEFITS is sent for both OneTapAdd and OneTapRemove, we have added a check on ctas to show gradient
        isCTATypePresent(meta?.ctas, CTATypes.ONE_TAP_REMOVE)
    ) {
        return BG_GRADIENTS.PEACH;
    } else if (isSwiggyBlack(planDetails)) {
        if (isNudgeTypeOneTap(meta) && isCTATypePresent(meta?.ctas, CTATypes.ONE_TAP_REMOVE)) {
            return BG_GRADIENTS.BROWN_LEFT;
        } else if (isNudgeTypeOneTap(meta) && isCTATypePresent(meta?.ctas, CTATypes.ONE_TAP_ADD)) {
            return BG_GRADIENTS.BROWN_TOP_RIGHT;
        } else {
            return BG_GRADIENTS.BROWN;
        }
    }
};

export const subConfigBuilder = (meta: RenderingDetailsMeta, key: string, planDetails: PlanDetails) => {
    // show one logo in backgrop only if it is regular buy nudge for one flagship
    // or, it is the benefit list widget (active one flagship user) or it is one tap nudge with no ctas [Cart Upsell Case]
    const canShowLogo =
        (renderRedGradientWithBgLogo(key, meta) && isOneFlagship(planDetails)) ||
        isOneBlackActiveUserBenefit(key, meta, planDetails);
    // logoLink is just a logo picker and the visibility control is handled canShowLogo flag
    const logoLink = isSwiggyBlack(meta?.active_plan_details) ? ONELOGO_LINK.BLACK : ONELOGO_LINK.ONE;

    return {
        showOneBgLogo: canShowLogo,
        // make this based on delivery_tag_info field as it comes for Active/OneTapRemove for OneLite
        showHalfBorderBelowHeader: false, // not needed as per new UI
        bgGradient: bgGradientSelector(meta, key, planDetails),
        bgLogoLink: logoLink,
    };
};

export const getPlanPurchaseCTAConfig = (planPurchaseInfoCTAType: CTATypes) => {
    return {
        isViewAllBenefitsCTAType: planPurchaseInfoCTAType === CTATypes.BENEFITS_LIST_DEEPLINK,
        isDeeplinkCTAType: planPurchaseInfoCTAType === CTATypes.OPEN_SUPER_V3_DEEPLINK,
    };
};

export const isCTATypePresent = (ctas: CTA[], type: CTATypes) => {
    return !isEmpty(ctas) && ctas.filter(cta => cta.type === type).length > 0;
};

export const displayTextExtractor = (itemData: RenderingDetails) => {
    return itemData?.display_text || itemData?.meta?.title || "";
};

export const isEOAvailableHelper = (benefits: Benefit[]) => {
    return benefits?.some(
        (benefit: Benefit) =>
            benefit?.attribute_values &&
            benefit?.attribute_values?.behaviour &&
            benefit?.attribute_values?.behaviour?.toUpperCase() === MovNotMet.MOV_NOT_MET,
    );
};
